import { useEffect, useState, ReactNode } from "react";
import type { Language } from "@/hooks/useTranslations";
import type { Currency } from "@/hooks/useCurrency";

interface GeoLocationProviderProps {
  children: ReactNode;
}

// Map country codes to languages
const countryToLanguage: Record<string, Language> = {
  JP: "ja",
  KR: "ko",
};

// Map country codes to currencies
const countryToCurrency: Record<string, Currency> = {
  US: "USD",
  GB: "GBP",
  UK: "GBP",
  DE: "EUR",
  FR: "EUR",
  IT: "EUR",
  ES: "EUR",
  NL: "EUR",
  BE: "EUR",
  AT: "EUR",
  IE: "EUR",
  PT: "EUR",
  GR: "EUR",
  FI: "EUR",
  JP: "JPY",
  KR: "KRW",
};

/**
 * Provider that detects user location via timezone on first visit
 * and sets language + currency automatically.
 * Uses browser timezone only — no external API calls — to avoid
 * console errors (403/429) that hurt Lighthouse scores.
 */
const GeoLocationProvider = ({ children }: GeoLocationProviderProps) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const detectLocation = () => {
      // Check if user has already been detected or has manual preferences
      const hasDetected = localStorage.getItem("geo_detected");
      
      if (hasDetected) {
        setInitialized(true);
        return;
      }

      // Check if user has manual preferences set
      const storedLanguage = localStorage.getItem("language");
      const storedCurrency = localStorage.getItem("currency");

      // Timezone-based detection (no network calls)
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let detectedLanguage: Language = "en";
        let detectedCurrency: Currency = "USD";

        if (timezone.startsWith("Asia/Tokyo")) {
          detectedLanguage = "ja";
          detectedCurrency = "JPY";
        } else if (timezone.startsWith("Asia/Seoul")) {
          detectedLanguage = "ko";
          detectedCurrency = "KRW";
        } else if (timezone.startsWith("Europe/London") || timezone.startsWith("Europe/Belfast")) {
          detectedCurrency = "GBP";
        } else if (timezone.startsWith("Europe/")) {
          detectedCurrency = "EUR";
        }
        
        let needsReload = false;
        
        if (!storedLanguage && detectedLanguage !== "en") {
          localStorage.setItem("language", detectedLanguage);
          needsReload = true;
        }
        if (!storedCurrency && detectedCurrency !== "USD") {
          localStorage.setItem("currency", detectedCurrency);
          needsReload = true;
        }
        
        localStorage.setItem("geo_detected", "true");
        
        if (needsReload) {
          window.location.reload();
          return;
        }
      } catch {
        // Use defaults
      }
      
      localStorage.setItem("geo_detected", "true");
      setInitialized(true);
    };

    detectLocation();
  }, []);

  // Show nothing while detecting (prevents flash)
  // Actually, we want to render immediately for better UX
  return <>{children}</>;
};

export default GeoLocationProvider;
